import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Coins, ShoppingBag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_COLOR = {
  MERCH: "#CCFF00", PT: "#FF6B35", RECOVERY: "#7DD3FC", GUEST_PASS: "#A78BFA", CASH: "#00E676",
};

export default function RedeemDialog({ trigger }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: balance } = useQuery({
    queryKey: ["my-reps"],
    queryFn: async () => (await apiClient.get("/me/reps")).data,
    enabled: open,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["redemption-items"],
    queryFn: async () => (await apiClient.get("/redemption-items")).data,
    enabled: open,
  });

  const redeem = useMutation({
    mutationFn: async (item_id) => (await apiClient.post("/redemptions", { item_id })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["my-reps"] });
      qc.invalidateQueries({ queryKey: ["customer-me"] });
      qc.invalidateQueries({ queryKey: ["my-redemptions"] });
      toast.success("Request submitted — pending Super Admin approval");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Redemption failed"),
  });

  const myBalance = balance?.balance ?? 0;
  const currency = balance?.currency_name || "REPS";
  const nextExp = balance?.next_expiry;
  const daysToExpiry = nextExp ? Math.ceil((new Date(nextExp.expires_at) - new Date()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border-zinc-800 text-white max-w-2xl" data-testid="redeem-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase tracking-tight flex items-center gap-2">
            <ShoppingBag size={22} className="text-volt" /> Redeem your {currency}
          </DialogTitle>
        </DialogHeader>
        <div className="bg-volt/10 border border-volt/30 px-4 py-3 rounded-sm flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400">Current Balance</div>
            <div className="font-display text-4xl font-black text-volt" data-testid="my-reps-balance">{myBalance} <span className="text-base text-zinc-500">{currency}</span></div>
          </div>
          <div className="text-right text-xs text-zinc-400">
            ≈ ₹{balance?.approx_value_inr ?? 0}
            <div className="text-[10px] text-zinc-600">1 {currency} = ₹{balance?.rupee_value_per_rep ?? 1}</div>
          </div>
        </div>

        {nextExp && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs uppercase tracking-[0.16em] px-3 py-2 rounded-sm flex items-center gap-2">
            <AlertTriangle size={14} /> {nextExp.amount} {currency} expire in {daysToExpiry} days ({new Date(nextExp.expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}). Redeem before then.
          </div>
        )}

        <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-semibold">
          Note · all redemptions require Super Admin approval before REPS are debited
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto scrollbar-thin">
          {items.map((it) => {
            const canAfford = myBalance >= it.reps_cost;
            return (
              <div key={it.id} data-testid={`redeem-item-${it.id}`}
                className={`bg-zinc-950 border rounded-sm p-3 flex flex-col ${canAfford ? "border-zinc-900 hover:border-volt" : "border-zinc-900 opacity-60"}`}>
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded-sm self-start"
                  style={{ background: `${CATEGORY_COLOR[it.category]}22`, color: CATEGORY_COLOR[it.category] }}>{it.category}</span>
                <div className="font-display text-lg font-black uppercase tracking-tight mt-2">{it.name}</div>
                <div className="text-xs text-zinc-400 mt-1 flex-1">{it.description}</div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="font-display text-2xl font-black text-volt">{it.reps_cost} <span className="text-[10px] text-zinc-500 font-sans">{currency}</span></div>
                  <Button size="sm" disabled={!canAfford || redeem.isPending} onClick={() => redeem.mutate(it.id)}
                    data-testid={`redeem-${it.id}`}
                    className="bg-volt hover:bg-volt-hover text-black font-bold uppercase">
                    {canAfford ? "Request" : "Locked"}
                  </Button>
                </div>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="col-span-2 text-zinc-600 text-sm py-12 text-center">
              <AlertTriangle size={20} className="mx-auto mb-2 text-zinc-700" />
              Catalog is empty — ask your Super Admin to add items.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
